import { currencyFormatter } from '@/helpers/currencyFormatter';
import { DRAWER_WIDTH } from '@/hooks/useGetDrawerMarginStyles';
import { useGetQuotesQuery } from '@/services/quotes';
import { useGetCurrentDesignQuery } from '@/services/systemDesign';
import { Alert, Box, Button, Drawer, FormikRadioGroup, useBreakpoint } from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { SyntheticEvent, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { HVACProductPricingResponse, ProgramType, QuoteStatus } from 'types';

import FormSectionHeader from '../Forms/FormSectionHeader';

import { FormResultSection } from './HVACQuoteCalculator.components';

interface QuoteDrawerProps {
  isOpen: boolean;
  handleCreateQuote: (values: { term: string; rate: string }) => void;
  systemDesignCost: number;
  isLoading: boolean;
  isPricingLoading: boolean;
  pricingData: HVACProductPricingResponse[];
  ratesAndTerms?: {
    terms: { label: string; value: string }[];
    rates: { label: string; value: string }[];
  };
  toggleDrawer: () => void;
  programType: ProgramType;
}

export const QuoteDrawer = ({
  isOpen,
  handleCreateQuote,
  systemDesignCost,
  isLoading,
  isPricingLoading,
  pricingData,
  ratesAndTerms,
  toggleDrawer,
  programType,
}: QuoteDrawerProps) => {
  const { id } = useParams<{ id: any }>();
  const { isPhone } = useBreakpoint();
  const { data: systemDesign, isError } = useGetCurrentDesignQuery({
    id,
    programType,
  });
  const { data: quotes = [] } = useGetQuotesQuery({ id, programType });

  const hasActiveQuote = quotes?.some((quote) => quote.status === QuoteStatus.active);

  const initialValues = useMemo(() => {
    const values = {
      term: '',
      rate: '',
    };
    if (
      !ratesAndTerms ||
      !ratesAndTerms?.terms ||
      !ratesAndTerms?.rates ||
      !ratesAndTerms?.terms?.length ||
      !ratesAndTerms?.rates?.length
    ) {
      return values;
    }
    if (ratesAndTerms.rates.length && ratesAndTerms.terms.length === 1) {
      values.term = ratesAndTerms.terms[0].value;
    }
    if (ratesAndTerms.rates.length && ratesAndTerms.rates.length === 1) {
      values.rate = ratesAndTerms.rates[0].value;
    }
    return values;
  }, [ratesAndTerms]);

  return (
    <Drawer
      isOpen={isOpen}
      title="System Pricing"
      onDismiss={toggleDrawer}
      ariaLabel="drawer component example"
      hideOverlay={isPhone ? false : true}
      width={`${DRAWER_WIDTH}px`}
      placement={isPhone ? 'bottom' : 'right'}
    >
      <Box padding="lg" height={isPhone ? '100vh' : 'auto'}>
        {!systemDesign || isError ? (
          <Box>A system design is required to create a quote.</Box>
        ) : (
          <Formik initialValues={initialValues} onSubmit={handleCreateQuote} enableReinitialize={true}>
            {({ setFieldValue, isSubmitting, values }) => (
              <Form>
                <Box
                  direction="row"
                  gap="xs"
                  alignItems="center"
                  borderWidth="0 0 xs 0"
                  borderColor="separator"
                  id="quote-section"
                  padding="0 lg lg 0"
                >
                  <Box fontWeight="medium" fontSize="md">
                    Total System Design Cost:
                  </Box>
                  <Box fontWeight="bold" fontSize="md">
                    {currencyFormatter.format(systemDesignCost)}
                  </Box>
                </Box>
                <Box
                  borderWidth="0 0 xs 0"
                  borderColor="separator"
                  gap="md"
                  padding={{ base: 'lg 0' }}
                  direction="column"
                >
                  <FormSectionHeader title="Term (Contract Length)"></FormSectionHeader>
                  <Box display="block" width="100" childGap={{ base: 'lg', desktop: 'xl' }}>
                    <Field
                      component={FormikRadioGroup}
                      id="term"
                      name="term"
                      options={ratesAndTerms?.terms}
                      onChange={(data: SyntheticEvent<HTMLInputElement>) => {
                        const selectedValue = (data.target as HTMLInputElement).value;
                        if (typeof selectedValue === 'undefined') {
                          return;
                        }
                        setFieldValue('term', selectedValue);
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  borderWidth="0 0 xs 0"
                  borderColor="separator"
                  gap="md"
                  padding={{ base: 'lg 0' }}
                  direction="column"
                >
                  <FormSectionHeader title="Escalator Rate"></FormSectionHeader>
                  <Box display="block" width="100" childGap={{ base: 'lg', desktop: 'xl' }}>
                    <Field
                      component={FormikRadioGroup}
                      id="rate"
                      name="rate"
                      options={ratesAndTerms?.rates}
                      onChange={(data: SyntheticEvent<HTMLInputElement>) => {
                        const selectedValue = (data.target as HTMLInputElement).value;

                        if (typeof selectedValue === 'undefined') {
                          return;
                        }
                        setFieldValue('rate', selectedValue);
                      }}
                    />
                  </Box>
                </Box>
                <FormResultSection data={pricingData} />
                {hasActiveQuote ? (
                  <Alert
                    variant="warning"
                    message="An active quote exists. Please void the quote and contract to create a new quote"
                  />
                ) : null}
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  alignItems={{
                    base: 'stretch',
                    tablet: 'flex-end',
                  }}
                  justifyContent={{
                    tablet: 'flex-end',
                  }}
                  childGap="sm"
                  style={{ flexShrink: 0 }}
                  padding={{ base: 'lg', desktop: 'lg 0 0 0' }}
                >
                  <Button
                    variant="secondary"
                    tone="neutral"
                    size="md"
                    onClick={toggleDrawer}
                    isDisabled={isLoading}
                    isLoading={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="md"
                    variant="primary"
                    type="submit"
                    isDisabled={
                      isPricingLoading || values.rate === undefined || values.term === undefined || hasActiveQuote
                    }
                    isLoading={isSubmitting}
                  >
                    Create Quote
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Drawer>
  );
};
